import getStreamUpdatesByEntity from './getStreamUpdates';
import { STREAM_ABORTED_ERROR, STREAM_TOKEN_NOT_FOUND_CODE } from './constants';
import type { StreamEntitySlug } from './entities';
import type StreamUpdatesManager from './streamUpdatesManager';
import type { StreamUpdateResponse } from './types';

export const isStaleCursorError = (error: any): boolean =>
  error?.response?.status === 410 && error.response.data?.code === STREAM_TOKEN_NOT_FOUND_CODE;

export const fetchEntityUpdates = async ({
  entity,
  initialToken,
  workspaceId,
  manager,
}: {
  entity: StreamEntitySlug;
  initialToken?: string;
  workspaceId?: string;
  manager: StreamUpdatesManager;
}): Promise<{ entity: StreamEntitySlug; token: string; items: any[] }> => {
  const aggregated: any[] = [];
  let nextToken = initialToken || '';
  let resync = false;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (manager.isClosed) {
      throw new Error(STREAM_ABORTED_ERROR);
    }

    let response: StreamUpdateResponse;

    try {
      response = await getStreamUpdatesByEntity({
        entity,
        updateToken: nextToken,
        workspaceId,
        resync,
      });
    } catch (error) {
      // Only a request that carried a cursor can have a stale one. A tokenless request that
      // still fails is a real error, and retrying it would loop.
      if (!nextToken || !isStaleCursorError(error)) {
        throw error;
      }

      // The server dropped our cursor (e.g. the device sat idle). The session is fine and the
      // local rows are kept, so restart this entity's stream with resync=true: that makes the
      // server deliver deletions that happened while the cursor was dead.
      aggregated.length = 0;
      nextToken = '';
      resync = true;
      continue;
    }

    resync = false;
    nextToken = response.update_token;

    if (!response.items?.length) {
      break;
    }

    aggregated.push(...response.items);
  }

  return { entity, token: nextToken, items: aggregated };
};
